import { classNames } from '@/utils'

import React, { useEffect, useRef, useState } from 'react'

import { DataTableColumn } from './DataTable'

interface ColumnVisibilityDropdownProps<Item, ColumnKeys> {
  columns: DataTableColumn<Item, ColumnKeys>[]
  onColumnVisibilityChange: (accessorKey: ColumnKeys) => void
  disabledColumns?: ColumnKeys[]
  label?: string | React.ReactNode
}

export function ColumnVisibilityDropdown<Item, ColumnKeys>(
  props: ColumnVisibilityDropdownProps<Item, ColumnKeys>
) {
  const { label = 'Columns', columns, onColumnVisibilityChange, disabledColumns = [] } = props
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className="inline-flex h-7 items-center gap-1.5 rounded-full border border-input bg-card px-3 text-xs font-medium text-ink hover:bg-subdued focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-52 rounded-lg bg-popover p-1.5 shadow-dropdown">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Columns</div>
          <div className="space-y-0.5">
            {columns.map((column, index) => {
              const isDisabled = disabledColumns.includes(column.accessorKey)
              const label = column.columnLabel?.() || column.accessorKey

              return (
                <label
                  key={index}
                  className={classNames(
                    'flex h-8 items-center gap-2 rounded-md px-2 hover:bg-accent',
                    isDisabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={!column.hidden}
                    onChange={() => !isDisabled && onColumnVisibilityChange(column.accessorKey)}
                    disabled={isDisabled}
                    className="size-4 accent-primary"
                  />
                  <span className="text-sm text-ink">
                    {typeof label === 'string' ? label : 'Column'}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
